import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function PrivacyPage() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy — React-City</title>
        <meta name="description" content="Informativa sulla privacy di React-City — Movimento del Dono." />
      </Helmet>

      <div className="min-h-screen bg-white dark:bg-[#1b1b1f] transition-colors">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Breadcrumb */}
          <nav className="mb-8 text-sm text-muted">
            <Link to="/" className="hover:text-primary-500">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-heading">Privacy Policy</span>
          </nav>

          <h1 className="text-3xl md:text-4xl font-bold text-heading mb-2">
            Informativa sulla Privacy
          </h1>
          <p className="text-muted mb-10">
            Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>

          <div className="prose prose-surface dark:prose-invert max-w-none space-y-8">
            {/* 1. Titolare */}
            <section>
              <h2 className="text-xl font-semibold text-heading mb-3">1. Titolare del trattamento</h2>
              <div className="card-flat">
                <p className="text-sm text-muted">
                  Il titolare del trattamento dei dati personali è <strong className="text-heading">React-City — Movimento del Dono</strong>.
                  Per qualsiasi richiesta relativa alla privacy è possibile scrivere a{' '}
                  <a href="mailto:privacy@react-city.it" className="text-primary-500">privacy@react-city.it</a>.
                </p>
              </div>
            </section>

            {/* 2. Dati raccolti */}
            <section>
              <h2 className="text-xl font-semibold text-heading mb-3">2. Dati personali raccolti</h2>
              <p className="text-sm text-muted mb-3">Raccogliamo i seguenti dati:</p>
              <ul className="space-y-2 text-sm text-muted">
                <li className="flex items-start gap-2">
                  <span className="text-primary-500 mt-0.5">•</span>
                  <span><strong className="text-heading">Dati identificativi:</strong> nome, cognome, indirizzo email forniti in fase di registrazione.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-500 mt-0.5">•</span>
                  <span><strong className="text-heading">Dati di navigazione:</strong> indirizzo IP, tipo di browser, pagine visitate, timestamp di accesso.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-500 mt-0.5">•</span>
                  <span><strong className="text-heading">Dati finanziari:</strong> storico delle donazioni e dei payout registrati nel ledger interno (nessun dato carta di credito conservato lato server).</span>
                </li>
              </ul>
            </section>

            {/* 3. Finalità */}
            <section>
              <h2 className="text-xl font-semibold text-heading mb-3">3. Finalità del trattamento</h2>
              <div className="grid gap-3">
                {[
                  { icon: '🔐', title: 'Autenticazione e sicurezza', desc: 'Gestione account, login, protezione da accessi non autorizzati.' },
                  { icon: '�️', title: 'Erogazione del servizio', desc: 'Funzionamento del Movimento del Dono: assegnazione box, gestione payout, ledger contabile.' },
                  { icon: '📊', title: 'Miglioramento del servizio', desc: 'Analisi aggregate e anonime per migliorare l\'esperienza utente.' },
                  { icon: '📋', title: 'Obblighi di legge', desc: 'Adempimento di obblighi normativi e fiscali.' },
                ].map((item, i) => (
                  <div key={i} className="card-flat flex items-start gap-3">
                    <span className="text-xl">{item.icon}</span>
                    <div>
                      <p className="font-medium text-heading text-sm">{item.title}</p>
                      <p className="text-xs text-muted">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* 4. Base giuridica */}
            <section>
              <h2 className="text-xl font-semibold text-heading mb-3">4. Base giuridica</h2>
              <p className="text-sm text-muted">
                Il trattamento dei dati si fonda su: (a) il consenso dell'interessato per cookie analitici;
                (b) l'esecuzione di un contratto per l'erogazione del servizio;
                (c) il legittimo interesse per la sicurezza della piattaforma;
                (d) obblighi di legge per gli adempimenti normativi.
              </p>
            </section>

            {/* 5. Conservazione */}
            <section>
              <h2 className="text-xl font-semibold text-heading mb-3">5. Periodo di conservazione</h2>
              <p className="text-sm text-muted">
                I dati personali vengono conservati per il tempo strettamente necessario alle finalità
                sopra indicate e comunque non oltre 5 anni dalla cessazione dell'account.
                I dati del ledger contabile sono conservati per 10 anni come previsto dalla normativa fiscale.
              </p>
            </section>

            {/* 6. Diritti */}
            <section>
              <h2 className="text-xl font-semibold text-heading mb-3">6. I tuoi diritti (GDPR Artt. 15-22)</h2>
              <div className="card-flat">
                <div className="grid sm:grid-cols-2 gap-2 text-sm text-muted">
                  {[
                    'Diritto di accesso (Art. 15)',
                    'Diritto di rettifica (Art. 16)',
                    'Diritto alla cancellazione (Art. 17)',
                    'Diritto di limitazione (Art. 18)',
                    'Diritto alla portabilità (Art. 20)',
                    'Diritto di opposizione (Art. 21)',
                  ].map((right, i) => (
                    <p key={i} className="flex items-center gap-2">
                      <span className="w-5 h-5 bg-primary-500/15 text-primary-500 rounded-full flex items-center justify-center text-xs font-bold">
                        ✓
                      </span>
                      {right}
                    </p>
                  ))}
                </div>
                <p className="mt-4 text-xs text-muted">
                  Per esercitare i diritti scrivere a{' '}
                  <a href="mailto:privacy@react-city.it" className="text-primary-500">privacy@react-city.it</a>.
                  In caso di mancata risposta entro 30 giorni, è possibile proporre reclamo al Garante per la protezione dei dati personali.
                </p>
              </div>
            </section>

            {/* 7. Cookie */}
            <section>
              <h2 className="text-xl font-semibold text-heading mb-3">7. Cookie</h2>
              <p className="text-sm text-muted">
                Per informazioni dettagliate sull'utilizzo dei cookie, consulta la nostra{' '}
                <Link to="/cookie-policy" className="text-primary-500 underline">Cookie Policy</Link>.
              </p>
            </section>
          </div>

          {/* Back */}
          <div className="mt-12 pt-8 border-t divider">
            <Link to="/" className="btn-secondary">
              ← Torna alla home
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
